import ks from "../ReUsableTS/kBrowserShared";
import { kUIText } from "./kUIText";
import { AsKTimeOrNull, kHTMLEditableColType } from "./kEditableTableTypes";
import type { kEditableTableCell } from "./kEditableTableCell";
import type { kTime } from "../ReUsableTS/kBrowserObjects";
import type { kEditableTableRow } from "./kEditableTableRow";

// Member bodies of kEditableTableCell moved here verbatim (this -> Self) by the size split of 2026-10-20; every kEditableTableCell member keeps its signature and delegates here.
export class kEditableTableCellValue {
    public static _FireDirtiedCallback(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ParentTable && Self.ParentTable.CellDirtiedCB && Self.ParentTable.PasteInProgress === false) {
                ks.DoCallBack(Self.ParentTable.CellDirtiedCB, Self, false);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._FireDirtiedCallback");
        }
    }

    public static SetClean(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Dirty = false;
            Self.Cell.style.fontWeight = "normal";
            Self.OrgValue = Self.CurrentValue;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue.SetClean");
            return;
        }
    }

    public static SetNewValue(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // Committing a value re-renders the cell in read mode, so release any
            // edit-time width pin _EditSelect left on it. This covers the common
            // path where an option is picked in the dropdown (change ->
            // ChoiceDDChanged -> SetNewValue) and the <select> is swapped for text
            // WITHOUT going through SetCellReadMode - otherwise the pin lingered
            // until the next cell interaction, keeping the column from shrinking to
            // a shorter committed value. Harmless for other column types (they
            // never set an inline min-width). Runtime-found by adversarial verify.
            if (Self.Cell !== null) { Self.Cell.style.minWidth = ""; Self.Cell.style.minHeight = ""; }
            if (Self["_PaintCustomRendered"](NewValue, SetDirty) === true) { return; }
            if(Self.Cell && Self.ParentTable && Self.ColDef){
                Self["_PaintNewValue"](NewValue, SetDirty);
            }
            Self["_ApplyValidation"]();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue.SetNewValue");
            return;
        }
    }

    /** Moved from kEditableTableCell._PaintNewValue (size split); the class member delegates here. */
    public static _PaintNewValue(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            switch (Self.ColDef.Type) {
                case kHTMLEditableColType.Date: Self["_SetValueDate"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Color: Self["_SetValueColor"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Number: Self["_SetValueNumber"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Currency: Self["_SetValueCurrency"](NewValue, SetDirty); break;
                case kHTMLEditableColType.SelectMulti: Self["_SetValueSelectMulti"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Checkbox: Self["_SetValueCheckbox"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Radio: Self["_SetValueRadio"](NewValue, SetDirty); break;
                case kHTMLEditableColType.RichHtml: Self["_SetValueRichHtml"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Text: Self["_SetValueText"](NewValue, SetDirty); break;
                case kHTMLEditableColType.Select: Self["_SetValueSelect"](NewValue, SetDirty); break;
                default: Self["_SetValueDefault"](NewValue, SetDirty); break;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._PaintNewValue");
            return;
        }
    }

    /** Moved from kEditableTableCell._SetValueDate (size split); the class member delegates here. */
    public static _SetValueDate(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(NewValue){
                Self.Cell.textContent = NewValue.ToLocalShortDate();
            } else {
                Self.Cell.textContent = "";
            }
            const OrgValDate:kTime |null = AsKTimeOrNull(Self.OrgValue);
            const NewDate:kTime |null = AsKTimeOrNull(NewValue);
            // Three real transitions need CurrentValue + dirty:
            //   null  -> kTime  (new-row first edit — was ignored
            //                    by the prior OrgValDate && NewDate
            //                    guard, dropping the picked kTime
            //                    on the floor),
            //   kTime -> null   (clear date),
            //   kTime -> kTime  (with different ISO).
            // null -> null is clean.
            let DateChanged:boolean = false;
            if(OrgValDate === null && NewDate !== null){ DateChanged = true; }
            else if(OrgValDate !== null && NewDate === null){ DateChanged = true; }
            else if(OrgValDate !== null && NewDate !== null){
                if(OrgValDate.ToISODate() !== NewDate.ToISODate()){ DateChanged = true; }
            }
            if(DateChanged){
                Self.CurrentValue = NewValue;
                if(SetDirty !== false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueDate");
        }
    }

    /** Moved from kEditableTableCell._SetValueColor (size split); the class member delegates here. */
    public static _SetValueColor(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // Swatch + hex/rgb tooltip; the colour value now lives in
            // CurrentValue (assigned unconditionally), not the cell
            // background, which the harvest reads.
            Self._PaintColorSwatch(ks.Nz(NewValue));
            Self.CurrentValue = NewValue;
            if(NewValue !== Self.OrgValue){
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueColor");
        }
    }

    /** Moved from kEditableTableCell._SetValueNumber (size split); the class member delegates here. */
    public static _SetValueNumber(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(NewValue !== Self.OrgValue){
                Self.CurrentValue = ks.N0(NewValue);
                Self.Cell.textContent = kUIText.Count(Self.CurrentValue, 6);
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
            if(Self.ParentTable){ Self.ParentTable.ApplyColumnDataBars(Self.ColumnIndex); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueNumber");
        }
    }

    /** Moved from kEditableTableCell._SetValueCurrency (size split); the class member delegates here. */
    public static _SetValueCurrency(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(NewValue !== Self.OrgValue){
                if(typeof(NewValue) === "string"){
                    Self.CurrentValue = ks.NumberFromCurrency(NewValue);
                }else{
                    Self.CurrentValue = ks.N0(NewValue);
                }
                Self.Cell.textContent = ks.LocalCurrencyNumber(Self.CurrentValue);
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
            if(Self.ParentTable){ Self.ParentTable.ApplyColumnDataBars(Self.ColumnIndex); }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueCurrency");
        }
    }

    /** Moved from kEditableTableCell._SetValueSelectMulti (size split); the class member delegates here. */
    public static _SetValueSelectMulti(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.CurrentValue = NewValue;
            Self._PaintMultiChips(ks.Nz(NewValue));
            if(NewValue !== Self.OrgValue){
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueSelectMulti");
        }
    }

    /** Moved from kEditableTableCell._SetValueCheckbox (size split); the class member delegates here. */
    public static _SetValueCheckbox(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.innerHTML = Self._CheckboxHtml(!!NewValue);
            Self.Cell.style.textAlign = "center";
            // CurrentValue is the boolean source of truth (harvest,
            // copy and Excel export all read it, not the cell text),
            // so assign it unconditionally — a toggle back to OrgValue
            // must not leave it stale the way the Radio branch's
            // dirty-only assignment can.
            const Was: boolean = Self.CurrentValue === true;
            Self.CurrentValue = !!NewValue;
            if(NewValue !== Self.OrgValue){
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
                // A box toggled BACK to its original value is still a change the host must hear of.
                if (Was !== Self.CurrentValue && SetDirty !== false) { Self["_NotifyToggledBack"](); }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueCheckbox");
        }
    }

    /** Moved from kEditableTableCell._SetValueRadio (size split); the class member delegates here. */
    public static _SetValueRadio(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.innerHTML = Self._RadioHtml(!!NewValue);
            Self.Cell.style.textAlign = "center";
            // CurrentValue is the boolean source of truth — the
            // deselection loop below keys off it, and harvest/copy read
            // it, not the DOM. Assign it UNCONDITIONALLY (like the
            // Checkbox branch). Guarding it behind (NewValue !== OrgValue)
            // — as the dirty flag is — left the originally-selected row's
            // CurrentValue stale (false) after a toggle-off-then-reselect
            // of that same row: the radio painted ON but the model said
            // OFF, so a later click on another row could not find it to
            // clear it and two radios ended up selected at once.
            Self.CurrentValue = !!NewValue;
            if(NewValue){ Self["_DeselectOtherRadios"](); }
            if(NewValue !== Self.OrgValue){
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueRadio");
        }
    }

    /** Moved from kEditableTableCell._DeselectOtherRadios (size split); the class member delegates here. */
    public static _DeselectOtherRadios(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if(Self.ParentTable === null){ return; }
            let CurrOtherCell:kEditableTableCell;
            let CurrRow:kEditableTableRow;
            for(CurrRow of Self.ParentTable.Rows.Items){
                CurrOtherCell = CurrRow.Cells[Self.ColumnIndex];
                if(CurrOtherCell !== Self && CurrOtherCell.CurrentValue === true){
                    CurrOtherCell.SetNewValue(false);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._DeselectOtherRadios");
        }
    }

    /** Moved from kEditableTableCell._SetValueRichHtml (size split); the class member delegates here. */
    public static _SetValueRichHtml(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self["_PaintRichHtmlPreview"](ks.Nz(NewValue));
            Self.CurrentValue = ks.Nz(NewValue);
            if(Self.CurrentValue !== ks.Nz(Self.OrgValue)){
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueRichHtml");
        }
    }

    /** Moved from kEditableTableCell._SetValueText (size split); the class member delegates here. */
    public static _SetValueText(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.textContent = ks.Nz(NewValue);
            if(Self.Cell.textContent !== Self.OrgValue){
                Self.CurrentValue = NewValue;
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
            // Type-into-last-row UX: a USER entering a value in the
            // last row grows the grid by one blank. Programmatic
            // writes pass SetDirty === false (hydration/load) and
            // must NOT grow the grid — that interleaved one stray
            // blank row per data row when a host loaded a saved
            // answer row-by-row (each write landed in the then-last
            // row). Runtime-confirmed 2026-06-10 (ISO blank-rows bug).
            if(Self.ParentTable === null){ return; }
            if(SetDirty !== false && Self.RowIndex === Self.ParentTable.Rows.Items.length-1){ // Last row
                if(ks.Nz(NewValue) !== "" && Self.ParentTable.BlankRowsAllowed() === true){ // If not empty add a new row
                    Self.ParentTable.AddEmptyRows(1);
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueText");
        }
    }

    /** Moved from kEditableTableCell._SetValueSelect (size split); the class member delegates here. */
    public static _SetValueSelect(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // Paint the chosen option as a colour-coded chip and keep
            // the read-mode option tooltip (no-op when the column has no
            // tooltip for this value). Dirty is compared on NewValue —
            // not the chip's textContent — so the chip DOM is not
            // load-bearing for value tracking.
            Self._PaintSelectChip(ks.Nz(NewValue));
            Self["_ApplyOptionTooltip"](ks.Nz(NewValue));
            Self.CurrentValue = NewValue;
            if(ks.Nz(NewValue) !== ks.Nz(Self.OrgValue)){
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueSelect");
        }
    }

    /** Moved from kEditableTableCell._SetValueDefault (size split); the class member delegates here. */
    public static _SetValueDefault(Self: kEditableTableCell, NewValue:any, SetDirty?:boolean): void {
        if (ks.KErrorMode === true) { return; }
        try {
            Self.Cell.textContent = ks.Nz(NewValue);
            if(Self.Cell.textContent !== Self.OrgValue){
                Self.CurrentValue = NewValue;
                if(SetDirty!==false){
                    Self.SetDirty();
                }
            }else{
                Self.SetClean();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._SetValueDefault");
        }
    }

    // Sets (or clears) the cell's native hover tooltip from the column's
    // OptionTooltips map for the given option value. The PRIMARY surface for
    // per-option help on a Select column — hovering the cell in read mode
    // reveals what the selected value means. No-op when the column declared
    // no tooltip for this value, so un-annotated Select columns are unchanged.
    public static _ApplyOptionTooltip(Self: kEditableTableCell, Value: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Tip: string = ks.Nz(Self.ColDef.OptionTooltips[Value]);
            if (Tip === "") { Self.Cell.removeAttribute("title"); return; }
            Self.Cell.title = Tip;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._ApplyOptionTooltip");
        }
    }

    // Titles the <option> elements of the open edit dropdown from the same
    // OptionTooltips map (secondary surface — native <option> tooltip support
    // varies by browser, which is why the cell-level tooltip above is primary).
    public static _TitleSelectOptions(Self: kEditableTableCell, Select: HTMLSelectElement): void {
        if (ks.KErrorMode === true) { return; }
        try {
            const Map: { [k: string]: string } = Self.ColDef.OptionTooltips;
            let i: number = 0;
            while (i < Select.options.length) {
                const Opt: HTMLOptionElement = Select.options[i];
                const Tip: string = ks.Nz(Map[Opt.value]);
                if (Tip !== "") { Opt.title = Tip; }
                i = i + 1;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._TitleSelectOptions");
        }
    }

    public static _PaintRichHtmlPreview(Self: kEditableTableCell, Html: string): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // The value originates from the rich-text editor or an SP rich-text
            // column (same trust model as SP multiline rich fields), so it is
            // painted as markup. The CSS class flattens block elements to one
            // clamped line; maxWidth makes text-overflow effective in a td.
            Self.Cell.classList.add("kEdTblRichHtmlCell");
            if (Self.ColDef.Width > 0) {
                Self.Cell.style.maxWidth = Self.ColDef.Width + "px";
            }
            Self.Cell.innerHTML = Html;
            Self["_ValidationIcon"] = null; // innerHTML wipe removed it; _ApplyValidation re-adds
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._PaintRichHtmlPreview");
        }
    }

    // True when the row that owns this cell carries no user data anywhere — the
    // perpetual trailing "type here" blank, or an added-but-untouched row. Such a
    // row is scaffolding that harvest/codecs drop on save (kEditableTableRow.IsEmpty
    // / a consumer codec's IsBlank), so its required-but-empty cells must NOT be
    // flagged as validation errors.
    public static _OwningRowIsEmpty(Self: kEditableTableCell): boolean {
        if (ks.KErrorMode === true) { return false; }
        try {
            if (Self.ParentTable === null) { return false; }
            const OwningRow: kEditableTableRow = Self.ParentTable.Rows.Items[Self.RowIndex];
            if (ks.KNotNullOrUndefined(OwningRow) && OwningRow.IsEmpty() === true) { return true; }
            return false;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._OwningRowIsEmpty");
            return false;
        }
    }

    public static _ApplyValidation(Self: kEditableTableCell): void {
        if (ks.KErrorMode === true) { return; }
        try {
            if (Self.ColDef && Self.ColDef.ValidationCB) {
                // Skip validation on a fully-empty row: flagging the trailing
                // blank (or an untouched added row) with a red outline + warning
                // triangle is a false alarm, since that row is silently dropped on
                // save. A row that holds data in OTHER cells but is missing this
                // required value is genuinely incomplete and still flags.
                let Err: string | null = null;
                if (Self["_OwningRowIsEmpty"]() === false) {
                    Err = Self.ColDef.ValidationCB(Self.CurrentValue);
                }
                if (Err !== null) {
                    Self.Cell.style.outline = "2px solid #d13438";
                    Self.Cell.title = Err;
                    if (!Self["_ValidationIcon"]) {
                        Self["_ValidationIcon"] = document.createElement("span");
                        Self["_ValidationIcon"].className = "kValidationIcon";
                        Self["_ValidationIcon"].textContent = "\u26A0";
                        Self.Cell.appendChild(Self["_ValidationIcon"]);
                    }
                } else {
                    Self.Cell.style.outline = "";
                    Self.Cell.title = "";
                    if (Self["_ValidationIcon"]) {
                        Self["_ValidationIcon"].remove();
                        Self["_ValidationIcon"] = null;
                    }
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._ApplyValidation");
        }
    }

    public static _TextEnterPrevent(Self: kEditableTableCell, Event:any): void {
        if (ks.KErrorMode === true) { return; }
        try {
            // Any edit to the cell text (fired on every beforeinput) promotes a
            // merely-selected "ready" cell to ACTIVE edit mode, so the caret keys
            // that follow the first keystroke act on the text (Excel: typing enters
            // edit mode). See kEditableTable._InlineEditActive.
            if (Self.ParentTable !== null) { Self.ParentTable._InlineEditActive = true; }
            if(['insertParagraph', 'insertLineBreak'].indexOf(Event.inputType)>-1){
                Event.preventDefault();
                Event.stopPropagation();
                Self.SelectNextDown();
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kEditableTableCellValue._TextEnterPrevent");
            return;
        }
    }
}
